import { useEffect, useState } from 'react';
import { X, Sparkles, Plus, RefreshCw } from 'lucide-react';
import { CardImage } from '@/components/CardImage';
import { InkDot } from '@/components/InkDot';
import { supabase } from '@/integrations/supabase/client';
import { useCards, type Card } from '@/hooks/useCards';
import type { DeckCard } from '@/hooks/useDecks';
import type { InkColor } from '@/lib/ink-utils';
import { toast } from 'sonner';

interface DeckSuggestSheetProps {
  open: boolean;
  onClose: () => void;
  deckName: string;
  ink1: InkColor;
  ink2: InkColor;
  cards: DeckCard[];
  onAdd: (card: Card) => void;
}

interface AiSuggestion {
  name: string;
  reason: string;
  card: Card | null;
}

function normalize(s: string) {
  return s.toLowerCase().replace(/['’`]/g, '').replace(/\s+/g, ' ').trim();
}

export function DeckSuggestSheet({ open, onClose, deckName, ink1, ink2, cards, onAdd }: DeckSuggestSheetProps) {
  const { data: catalog } = useCards();
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<AiSuggestion[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [overview, setOverview] = useState<string | null>(null);

  const ask = async () => {
    setLoading(true);
    setError(null);
    setSuggestions([]);
    setOverview(null);
    try {
      const decklist = cards
        .filter(c => c.card)
        .map(c => `${c.quantity}x ${c.card!.name}${c.card!.subtitle ? ` - ${c.card!.subtitle}` : ''} (⬡${c.card!.cost ?? 0}, ${c.card!.ink_color})`)
        .join('\n');
      const total = cards.reduce((s, c) => s + c.quantity, 0);

      const { data, error } = await supabase.functions.invoke('deck-suggest', {
        body: {
          deckName,
          inks: [ink1, ink2],
          totalCards: total,
          decklist,
        },
      });
      if (error) throw error;
      const raw = (data as any) ?? {};
      setOverview(typeof raw.overview === 'string' ? raw.overview : null);
      const names: { name: string; reason: string }[] = Array.isArray(raw.suggestions)
        ? raw.suggestions.slice(0, 8)
        : [];
      const matched: AiSuggestion[] = names.map(s => {
        const norm = normalize(s.name);
        const card =
          (catalog || []).find(c =>
            [ink1, ink2].includes(c.ink_color as InkColor) && normalize(c.name) === norm,
          ) ??
          (catalog || []).find(c =>
            [ink1, ink2].includes(c.ink_color as InkColor) && normalize(c.name).startsWith(norm),
          ) ??
          null;
        return { name: s.name, reason: s.reason, card };
      });
      setSuggestions(matched);
    } catch (err: any) {
      setError(err?.message || 'Could not fetch suggestions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && suggestions.length === 0 && !loading && !error) {
      ask();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center bg-background/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="animate-slide-up w-full max-w-[430px] max-h-[90vh] flex flex-col rounded-t-2xl border border-border bg-card"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <h3 className="font-display text-base font-semibold text-foreground">Scout's Picks</h3>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={ask}
              disabled={loading}
              className="flex h-8 w-8 items-center justify-center rounded-lg bg-secondary text-muted-foreground hover:text-foreground disabled:opacity-50"
              aria-label="Regenerate suggestions"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={onClose} className="text-muted-foreground" aria-label="Close">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {overview && (
            <p className="text-xs text-muted-foreground font-body italic border-l-2 border-primary/40 pl-2.5">
              {overview}
            </p>
          )}

          {loading && (
            <div className="py-10 text-center">
              <Sparkles className="h-6 w-6 text-primary mx-auto mb-2 animate-pulse" />
              <p className="text-xs text-muted-foreground font-body">Scout is analyzing your deck…</p>
            </div>
          )}

          {error && (
            <div className="rounded-xl border border-ink-ruby/30 bg-ink-ruby/5 p-3 text-xs text-ink-ruby font-body">
              {error}
            </div>
          )}

          {!loading && !error && suggestions.length === 0 && (
            <p className="text-xs text-muted-foreground font-body py-8 text-center">
              No suggestions yet. Try regenerating.
            </p>
          )}

          {suggestions.map((s, i) => (
            <div
              key={`${s.name}-${i}`}
              className="flex gap-2.5 rounded-xl border border-border bg-secondary/40 p-2.5 animate-fade-in"
            >
              {s.card ? (
                <CardImage
                  src={s.card.image_url}
                  alt={s.card.name}
                  inkColor={s.card.ink_color as InkColor}
                  className="w-12 aspect-[2/3] rounded flex-shrink-0"
                />
              ) : (
                <div className="w-12 aspect-[2/3] rounded bg-secondary flex items-center justify-center flex-shrink-0 text-muted-foreground text-[9px] font-body text-center px-1">
                  Not in catalog
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  {s.card && <InkDot ink={s.card.ink_color as InkColor} size="sm" />}
                  <p className="text-xs font-medium text-foreground font-body truncate">
                    {s.card?.name || s.name}
                  </p>
                </div>
                <p className="text-[10px] text-muted-foreground font-body mt-0.5 line-clamp-3">{s.reason}</p>
              </div>
              {s.card && (
                <button
                  onClick={() => {
                    onAdd(s.card!);
                    toast.success(`Added ${s.card!.name}`);
                  }}
                  className="self-center flex items-center gap-1 rounded-lg bg-primary px-2.5 py-1.5 text-[10px] font-medium text-primary-foreground font-body"
                >
                  <Plus className="h-3 w-3" /> Add
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
